/**
 * Content Section Switch - V2
 * 
 * A/B test wrapper for content section layouts.
 * Uses ExperimentalSection to switch between:
 * - Control: Traditional tab-based navigation (hidden content, show on tab click)
 * - Scrollable: All content visible, external sticky navigation targets sections
 * 
 * Navigation for variation is rendered at layout level:
 * - Desktop: StickyNavWithActions (in desktop-layout.tsx)
 * - Mobile: StickyHeaderTabs (in mobile-layout.tsx)
 */

import { AllowedExperimentKey } from '@tenants/types';
import { ExperimentalSection } from 'ui/experimental-section';

import type { AddressPageCommonData, AddressPageEventHandlers } from '../../../shared';
import { ContentSectionControl } from './content-section.control';
import { ContentSectionScrollable } from './content-section.scrollable';

export interface ContentSectionProps {
  data: AddressPageCommonData;
  handlers: AddressPageEventHandlers;
  className?: string;
  /** Whether map should allow user interactions (desktop: true, mobile: false) */
  isMapInteractive: boolean;
  /** Whether to show My Listing / My Link agent card */
  showMyListingAgent: boolean;
  /** Viewport identifier for unique section IDs (desktop/mobile) */
  viewport: 'desktop' | 'mobile';
  /** Ref to price history section for scrolling (variation only) */
  priceHistoryRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Content Section Switch
 * 
 * A/B test wrapper that renders either:
 * - Control: Traditional tab-based layout with hidden content
 * - Scrollable: All content visible with external navigation
 * 
 * Viewport-specific props (isMapInteractive, showMyListingAgent) are passed from layouts.
 * Control version ignores these props as it has its own internal viewport logic.
 * 
 * Experiment key: STICKY_HEADER_TABS
 * 
 * @example
 * // Force control: ?gb_force_dj-listing-sticky-tab-bar-redesign=0
 * // Force variation: ?gb_force_dj-listing-sticky-tab-bar-redesign=1
 */
export function ContentSection({ 
  data, 
  handlers, 
  className,
  isMapInteractive,
  showMyListingAgent,
  viewport,
  priceHistoryRef,
}: ContentSectionProps) {
  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.STICKY_HEADER_TABS}
      control={
        <ContentSectionControl 
          data={data} 
          handlers={handlers} 
          className={className}
          priceHistoryRef={priceHistoryRef}
        />
      }
      variation={
        <ContentSectionScrollable 
          data={data} 
          handlers={handlers} 
          className={className}
          isMapInteractive={isMapInteractive}
          showMyListingAgent={showMyListingAgent}
          viewport={viewport}
          priceHistoryRef={priceHistoryRef}
        />
      }
    />
  );
}

export default ContentSection;

// Re-export for direct usage if needed
export { ContentSectionControl } from './content-section.control';
export { ContentSectionScrollable } from './content-section.scrollable';
